import React, { useState } from 'react';
import { useTaxPortal } from '../../utils/useTaxPortal';
import TaxPortalLayout from '../../components/Common/TaxPortalLayout';
import UploadSection from './UploadSection';
import MetadataCard from './MetadataCard';
import TransactionTable from './TransactionTable';
import ExportSection from './ExportSection';
import ProcessingLoader from './ProcessingLoader';
import { apiFetch } from '../../utils/api';
import { toast } from 'react-toastify';
import { FaFileAlt, FaCheck } from 'react-icons/fa';
import './bank-statement.css';

const startParsingJob = async (files, account, msalInstance) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));

    const response = await apiFetch('/v1/bank-statement/parse/google', {
        method: 'POST',
        body: formData
    }, account, msalInstance);

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to start parsing job');
    }

    return response.json();
}

const checkParsingStatus = async (jobId, account, msalInstance) => {
    const response = await apiFetch(`/v1/bank-statement/parse/status/${jobId}`, {
        method: 'GET'
    }, account, msalInstance);

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to get job status');
    }

    return response.json();
}

const exportBankStatement = async (payload, account, msalInstance) => {
    const response = await apiFetch('/v1/bank-statement/export', {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
            'Content-Type': 'application/json'
        }
    }, account, msalInstance);

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to export bank statements');
    }

    return response.blob();
}

const BankStatementPage = () => {
    const portalState = useTaxPortal();
    const { account, msalInstance } = portalState;

    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [processingProgress, setProcessingProgress] = useState({ progress: 0, details: '' });
    
    // Array of statement objects: [{ statementId, fileName, error, metadata, transactions }]
    const [statements, setStatements] = useState([]);

    const handleProcess = async () => {
        if (!uploadedFiles.length) return;
        setIsProcessing(true);
        setProcessingProgress({ progress: 0, details: 'Starting job...' });

        try {
            const { job_id } = await startParsingJob(uploadedFiles, account, msalInstance);

            const poll = setInterval(async () => {
                try {
                    const statusResult = await checkParsingStatus(job_id, account, msalInstance);
                    setProcessingProgress({
                        progress: statusResult.progress,
                        details: statusResult.details
                    });

                    if (statusResult.status === 'completed') {
                        clearInterval(poll);
                        setIsProcessing(false);
                        setStatements(statusResult.result);
                        toast.success(`Successfully processed ${statusResult.result.length} file(s)`);
                    } else if (statusResult.status === 'failed') {
                        clearInterval(poll);
                        setIsProcessing(false);
                        toast.error(statusResult.details || 'Processing failed.');
                    }
                } catch (pollError) {
                    clearInterval(poll);
                    setIsProcessing(false);
                    toast.error('Lost connection to the server.');
                }
            }, 7000);

        } catch (error) {
            console.error('Parse error:', error);
            toast.error(error.message || 'An error occurred during processing');
            setIsProcessing(false);
        }
    };

    const triggerDownload = (blob, filename = 'statement.xlsx') => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    };

    const handleExportThis = async (stmt) => {
        setIsExporting(true);
        try {
            const enabledTxs = (stmt.transactions || []).filter(tx => tx.enabled !== false);
            const payload = {
                statements: [{
                    fileName: stmt.fileName,
                    metadata: stmt.metadata || {},
                    transactions: enabledTxs
                }]
            };
            const blob = await exportBankStatement(payload, account, msalInstance);
            const cleanName = stmt.fileName.replace(/\.pdf$/i, '');
            triggerDownload(blob, `${cleanName}_statement.xlsx`);
            toast.success(`Exported ${stmt.fileName}`);
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export statement');
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportAll = async () => {
        setIsExporting(true);
        try {
            const validStatementsPayload = statements
                .filter(stmt => !stmt.error)
                .map(stmt => ({
                    fileName: stmt.fileName,
                    metadata: stmt.metadata || {},
                    transactions: (stmt.transactions || []).filter(tx => tx.enabled !== false)
                }));

            if (!validStatementsPayload.length) {
                toast.error('No valid statements available to export');
                return;
            }

            const payload = { statements: validStatementsPayload };
            const blob = await exportBankStatement(payload, account, msalInstance);
            triggerDownload(blob, 'all_bank_statements.xlsx');
            toast.success('Exported all statements to Excel workbook');
        } catch (error) {
            console.error('Export all error:', error);
            toast.error('Failed to export all statements');
        } finally {
            setIsExporting(false);
        }
    };

    const handleStartOver = () => {
        setStatements([]);
        setUploadedFiles([]);
    };

    const updateStatementMetadata = (statementId, newMetadata) => {
        setStatements(prev => prev.map(s => 
            s.statementId === statementId ? { ...s, metadata: newMetadata } : s
        ));
    };

    const updateStatementTransactions = (statementId, newTransactions) => {
        setStatements(prev => prev.map(s => 
            s.statementId === statementId ? { ...s, transactions: newTransactions } : s
        ));
    };

    const isParsed = statements.length > 0;

    return (
        <TaxPortalLayout 
            title="PDF Bank Statement Converter" 
            description="Upload bank statement PDFs, review transactions, edit details, and export to Excel."
            portalState={portalState}
            isFluid={true}
        >
            <div className="bank-statement-container">
                {/* Workflow Stepper Bar */}
                <div className="stepper-container">
                    <div className={`step-item ${isParsed ? 'completed' : 'active'}`}>
                        <div className="step-badge">{isParsed ? <FaCheck size={12} /> : '1'}</div>
                        <div className="step-title">Upload Statements</div>
                    </div>
                    <div className="step-divider"></div>
                    <div className={`step-item ${isParsed ? 'active' : ''}`}>
                        <div className="step-badge">2</div>
                        <div className="step-title">Review & Edit Data</div>
                    </div>
                    <div className="step-divider"></div>
                    <div className={`step-item ${isParsed ? 'active' : ''}`}>
                        <div className="step-badge">3</div>
                        <div className="step-title">Export Excel</div>
                    </div>
                </div>

                {isProcessing ? (
                    <ProcessingLoader files={uploadedFiles} progress={processingProgress} />
                ) : !isParsed ? (
                    <UploadSection 
                        uploadedFiles={uploadedFiles} 
                        setUploadedFiles={setUploadedFiles} 
                        onProcess={handleProcess} 
                        isProcessing={false} // Loader is now handled outside
                    />
                ) : (
                    <>
                        {statements.length > 1 && (
                            <div className="bank-statement-card bg-white border shadow-sm p-3 mb-4 d-flex align-items-center gap-2">
                                <FaFileAlt className="text-primary" />
                                <h5 className="fw-bold text-dark m-0">Multi-Statement Workbook ({statements.length} Files)</h5>
                            </div>
                        )}

                        {statements.map((stmt, index) => (
                            <div key={stmt.statementId || index} className="mb-5">
                                {statements.length > 1 && (
                                    <div className="d-flex align-items-center justify-content-between mb-2 bg-white p-2 px-3 rounded-3 shadow-sm border">
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="badge bg-primary rounded-pill px-3 py-2 fs-6">{index + 1}</span>
                                            <h5 className="fw-bold text-dark m-0">{stmt.fileName}</h5>
                                        </div>
                                    </div>
                                )}

                                {stmt.error ? (
                                    <div className="alert alert-danger shadow-sm rounded-3">
                                        <strong>Error processing {stmt.fileName}:</strong> {stmt.error}
                                    </div>
                                ) : (
                                    <>
                                        <MetadataCard 
                                            metadata={stmt.metadata} 
                                            onChange={(updated) => updateStatementMetadata(stmt.statementId, updated)} 
                                        />
                                        <TransactionTable 
                                            transactions={stmt.transactions} 
                                            onUpdateTransactions={(updated) => updateStatementTransactions(stmt.statementId, updated)} 
                                        />
                                    </>
                                )}
                            </div>
                        ))}

                        {/* Single Export Section at the bottom — covers all uploaded statements */}
                        <ExportSection
                            transactions={statements.flatMap(s => s.transactions || [])}
                            onExportThis={statements.length === 1 ? () => handleExportThis(statements[0]) : handleExportAll}
                            onStartOver={handleStartOver}
                            isExporting={isExporting}
                            fileCount={statements.length}
                        />
                    </>
                )}
            </div>
        </TaxPortalLayout>
    );
};

export default BankStatementPage;
